import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/authContext';
import createAxiosInstance from '../../../utils/axiosConfig';
import { userManagementColumns } from '../../../utils/constants';
import AdminTables from '../../common/adminTables';
import { Alert as AlertType } from '../../../utils/interfaces';
import adminConfirmation from '../../common/modal/modalBody/adminConfirmation';
import { ConfirmationModal } from '../../common/modal';
import AdminLayout from '../../common/adminLayout';

const ManageSubAccounts = () => {
    const navigate = useNavigate();
    const { userData, token, logout, login } = useAuth();
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [selectedRowData, setSelectedRowData] = useState<any>(null);

	const [formData, setFormData] = useState<{
		results: [];
		total_count: number;
	}>({ results: [], total_count: 0 });
    const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(3);
    const [isLoading, setIsLoading] = useState(true);
    const [alert, setAlert] = useState<AlertType | null>(null);
	const [showModal, setShowModal] = useState(false);
	const [selectedAction, setSelectedAction] = useState<string | null>(null);
	const [selectedAccountType, setSelectedAccountType] = useState<number | null>(null);

    // User Action Handlers
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const action = e.target.value;
		if (action) {
			if (action === "visitAccount") {
				login_to_customer_account();
			}else{
				setSelectedAction(action);
				setShowModal(true);
			}
		}
	};

	const login_to_customer_account = async () => {
		try{
			const response = await createAxiosInstance().post("/login-to-customer", {
				email: selectedRowData.email,
			});
			store_parent_data(
				JSON.parse(localStorage.getItem(
					'userData') || "{}"), 
					localStorage.getItem('token') || ""
				);
			login(response.data.id, response.data.access_token, {...response.data, is_customer_account: true});
		}catch(error: any){
			console.log(error, "error")
		}
		// navigate("/");
	}

	const store_parent_data = (userData: any, token: string )=>{
		localStorage.setItem("parent_data", JSON.stringify({...userData}))
		localStorage.setItem("parent_token", token || "");
		localStorage.setItem("parent_id", userData.id);
	}

    const handleRowSelection = (rowData: any) => {
		if (rowData?.id !== selectedRowId) {
			setSelectedRowId(rowData?.id);
			setSelectedRowData(rowData);
			console.log(rowData, "Row data")
		} else {
			setSelectedRowId(null);
			setSelectedRowData(null);
		}
	};

    // Common error handling function
    const handleApiError = (error: any, context: string = "API request") => {
        if (error.status === 401 || error.status === 403) {
            logout();
            navigate("/");
        }
        console.error(`Error in ${context}:`, error);
        return { results: [], total_count: 0 };
    };

    // Data Grabbers
    const getCustomers = async () => {
		try {
			const response = await createAxiosInstance().get("/customers", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				},
			});
			return response;
		} catch (error: any) {
			return { data: handleApiError(error, "fetching customers") };
		}
	};

    const getFormData = async () => {
		try {
			setIsLoading(true);
			setFormData({ results: [], total_count: 0 });
			const response = await getCustomers();
			console.log(response.data, "response");
			setFormData(response.data);
		} catch (error: any) {
			handleApiError(error, "loading form data");
		} finally {
			setIsLoading(false);
		}
	};

    useEffect(() => {
		getFormData();
	}, [currentPage, itemsPerPage]);

	useEffect(() => {
		if (userData && userData?.account_type < 3) {
			navigate("/");
		}
	}, [userData]);

	// Unified action configuration
	const actionConfig: Record<string, { endpoint: string, resultMessage: string }> = {
		"changeAccountType": { endpoint: "/update-customer-account-access", resultMessage: "updated" },
		"delete": { endpoint: "/delete-customer", resultMessage: "deleted" },
		"suspend": { endpoint: "/suspend-customer", resultMessage: "suspended" },
		"reactivate": { endpoint: "/enable-customer", resultMessage: "reactivated" },
		'visitAccount': {endpoint: "/visit-account", resultMessage: "visited"}
	};

	const getActionEndpoint = (action: string): string => {
		return actionConfig[action]?.endpoint || `/users/${action}`;
	};
	// Helper function to build the payload
	const buildActionPayload = (action: string): Record<string, any> => {
		if (action === "changeAccountType") {
			return {
				new_account_type: selectedAccountType,
				user_id: selectedRowId,
			};
		}
		
		return { user_id: selectedRowId };
	};
	const getSuccessMessage = (action: string): string => {
		const resultMessage = actionConfig[action]?.resultMessage || action;
		return `Account with id ${selectedRowId} successfully ${resultMessage}`;
	};

	const handleModalConfirm = async () => {
		if (!selectedAction) return;
		
		try {
			const url = getActionEndpoint(selectedAction);
			const payload = buildActionPayload(selectedAction);
			
			await createAxiosInstance().put(url, payload);
			handleAlert(getSuccessMessage(selectedAction), "success");
			getFormData();
			setSelectedRowId(null);
		} catch (error: any) {
			handleApiError(error, "Error performing action");
		} finally {
			setShowModal(false);
			setSelectedAction(null);
		}
	};

	const handleModalCancel = () => {
		setShowModal(false);
		setSelectedAction(null);
	};

	const handleAlert = (message: string, type: "success" | "danger") => {
		setAlert({
			message,
			type,
			show: true
		});
	};

	// Action panel component
	const ActionPanel = () => (
		<>
			{selectedRowId && (
				<>
					<label htmlFor="floatingSelect">User Actions:</label>
					<select
						onChange={handleSelectChange}
						className="form-select mb-4"
						id="floatingSelect"
						aria-label="Floating label select example"
					>
						<option value="">Select an action</option>
						<option value="delete">Delete</option>
						{selectedRowData?.is_enabled && <option value="suspend">Suspend</option>}
						{!selectedRowData?.is_enabled && <option value="reactivate">Reactivate</option>}
						
						<option value="changeAccountType">
							Change Account Type
						</option>
						<option value="visitAccount">Visit Account</option>
					</select>
				</>
			)}
		</>
	);

	return (
		<AdminLayout
			title="Manage Customers"
			actionPanel={<ActionPanel />}
			formData={formData}
			currentPage={currentPage}
			setCurrentPage={setCurrentPage}
			itemsPerPage={itemsPerPage}
			setItemsPerPage={setItemsPerPage}
			alert={alert || undefined}
			setAlert={setAlert}
		>
			<div className="d-flex justify-content-start">
				<i
					className="bi bi-info-circle text-secondary cursor-pointer"
					style={{ cursor: "pointer" }}
				></i>
				<p className="text-secondary px-2">
					Click on a user row to perform relevant actions.
				</p>
			</div>
			<AdminTables
				data={formData.results}
				columns={userManagementColumns}
				rowSelectionEnabled={true}
				onRowSelect={handleRowSelection}
				isLoading={isLoading}
			/>
			<ConfirmationModal
				show={showModal}
				onClose={handleModalCancel}
				onConfirm={handleModalConfirm}
				title={'Confirm Action'}
				body={adminConfirmation({ 
					action: selectedAction ?? "",
					selectedAccountType,
					setSelectedAccountType })}
				maxHeight={selectedAction === "changeAccountType" ? "30vh" : "20vh"}
				maxWidth={'40vw'}
			/>
		</AdminLayout>
	);
};

export default ManageSubAccounts;
